import { useState, type FormEvent } from 'react';
import { PasswordField } from './password-field';

interface ForgotPasswordFormProps {
  /** Email a recovery code. Resolves once accepted (always, even for unknown emails). */
  onRequestCode: (email: string) => Promise<void>;
  /** Set a new password using the emailed code. */
  onConfirm: (email: string, code: string, newPassword: string) => Promise<void>;
  /** Return to the sign-in card (after success or cancel). */
  onDone: (message?: string) => void;
}

type Step = 'request' | 'confirm';

/**
 * Two-step forgot-password flow. Step 1 requests an emailed code; step 2 sets a
 * new password with it. Because the app client has preventUserExistenceErrors,
 * step 1 always advances to step 2 (no user enumeration).
 */
export function ForgotPasswordForm({ onRequestCode, onConfirm, onDone }: ForgotPasswordFormProps) {
  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleRequest(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onRequestCode(email.trim());
      setStep('confirm');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send a code.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirm(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await onConfirm(email.trim(), code.trim(), password);
      onDone('Password updated — sign in.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset password.');
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-sm">
      <div className="terminal-card overflow-hidden">
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <span className="win-dots relative block h-2.5 w-2.5" aria-hidden />
          <span className="ml-8 font-mono text-xs text-muted">admin — reset password</span>
        </div>

        {step === 'request' ? (
          <form onSubmit={handleRequest} className="space-y-4 p-6" noValidate>
            <p className="font-mono text-xs text-muted">
              Enter your email and we&apos;ll send a reset code.
            </p>

            <div>
              <label htmlFor="reset-email" className="font-mono text-sm text-muted">
                email
              </label>
              <input
                id="reset-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 w-full rounded-md border border-border bg-bg px-3 py-2 font-mono text-sm text-fg outline-none focus:border-accent"
              />
            </div>

            {error && (
              <p role="alert" className="font-mono text-sm text-red-400">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-md bg-accent px-4 py-2 font-mono text-sm font-medium text-bg transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? 'sending…' : 'send code'}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => onDone()}
                className="font-mono text-xs text-muted hover:text-accent"
              >
                back to sign in
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleConfirm} className="space-y-4 p-6" noValidate>
            <p className="font-mono text-xs text-muted">
              If an account exists for {email}, a code is on its way. Enter it below.
            </p>

            <div>
              <label htmlFor="reset-code" className="font-mono text-sm text-muted">
                code
              </label>
              <input
                id="reset-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                className="mt-1 w-full rounded-md border border-border bg-bg px-3 py-2 font-mono text-sm text-fg outline-none focus:border-accent"
              />
            </div>

            <PasswordField
              id="reset-new-password"
              label="new password"
              value={password}
              onChange={setPassword}
            />
            <PasswordField
              id="reset-confirm-password"
              label="confirm password"
              value={confirm}
              onChange={setConfirm}
            />

            {error && (
              <p role="alert" className="font-mono text-sm text-red-400">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-md bg-accent px-4 py-2 font-mono text-sm font-medium text-bg transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? 'resetting…' : 'reset password'}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => onDone()}
                className="font-mono text-xs text-muted hover:text-accent"
              >
                back to sign in
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
